var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Alert, Box, Button, Dialog, DialogContent, DialogError, DialogFooter, DialogHeader, DialogLoading, useToast, } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { DocumentType } from 'shared/domains/apollo/generated/types';
import { SupportedFileMimeTypes } from 'shared/domains/filesystem';
import { DownloadOption } from './DownloadOption';
import { StatusInformation } from './StatusInformation';
import { useDownloadFilesystemDocument } from './useDownloadFilesystemDocument';
import { useDownloadPleditorDocument } from './useDownloadPleditorDocument';
import { usePleditorDownloadAvailable } from './usePleditorDownloadAvailable';
export function DownloadDocumentDialog(props) {
    const { onClose, id } = props;
    const { addToast } = useToast();
    const { document, isPleditorDownloadAvailable, loading: loadingData, error: dataError, } = usePleditorDownloadAvailable(id);
    const { files, loading: loadingFiles, error: filesError, malwareScan } = useDownloadFilesystemDocument(id);
    const { downloadPleditorDocument, error: downloadError } = useDownloadPleditorDocument(document.type);
    const [downloading, setDownloading] = useState(false);
    const initialFilesLoading = !files.length && loadingFiles;
    const initialFilesError = !files.length && filesError;
    const loading = loadingData || initialFilesLoading;
    const error = Boolean(initialFilesError || dataError);
    const downloadingFilesError = Boolean(files.length) && filesError;
    const downloadingError = downloadingFilesError || downloadError;
    const pdfFile = files.find((file) => file.type === SupportedFileMimeTypes.PDF);
    const docxFile = files.find((file) => file.type === SupportedFileMimeTypes.DOCX);
    const isPleditorDocument = document.type === DocumentType.Composed || document.type === DocumentType.Freetext;
    const showPleditorOption = isPleditorDocument && isPleditorDownloadAvailable;
    const documentName = (document === null || document === void 0 ? void 0 : document.name) || '';
    const downloadWrapper = (promise) => __awaiter(this, void 0, void 0, function* () {
        setDownloading(true);
        try {
            yield promise;
            addToast({
                appearance: 'success',
                title: t({
                    message: `${documentName} downloaded successfully`,
                    comment: 'Download dialog success toast message',
                }),
            });
            onClose();
        }
        catch (_a) {
            return;
        }
        setDownloading(false);
    });
    const downloadPdf = () => {
        downloadWrapper(pdfFile === null || pdfFile === void 0 ? void 0 : pdfFile.fetch());
    };
    const downloadOriginalDocx = () => {
        downloadWrapper(docxFile === null || docxFile === void 0 ? void 0 : docxFile.fetch());
    };
    const downloadPleditorDocx = () => {
        downloadWrapper(downloadPleditorDocument(id, document.name));
    };
    if (!loading && !malwareScan.done && malwareScan.status) {
        return <StatusInformation status={malwareScan.status} onClose={onClose}/>;
    }
    const getContent = () => {
        if (loading) {
            return <DialogLoading />;
        }
        if (error) {
            return <DialogError />;
        }
        return (<Box flexDirection="column" gap="md">
        {pdfFile && (<DownloadOption type="pdf" title={pdfFile.name} downloading={downloading} onDownload={downloadPdf}/>)}

        {showPleditorOption && (<DownloadOption type="docx" title="DOCX" lockedReason={document.lockedReason} downloading={downloading} onDownload={downloadPleditorDocx}/>)}

        {docxFile && isPleditorDocument && !isPleditorDownloadAvailable && (<DownloadOption pleditorDownloadUnavailable type="docx" title={docxFile.name} downloading={downloading} onDownload={downloadOriginalDocx}/>)}

        {docxFile && !isPleditorDocument && (<DownloadOption type="docx" title={docxFile.name} downloading={downloading} onDownload={downloadOriginalDocx}/>)}

        {downloadingError && (<Alert appearance="danger" message={t({
                    message: 'Could not download the document, please try again',
                    comment: 'Error alert message while downloading document',
                })}/>)}
      </Box>);
    };
    return (<Dialog size="sm" onClose={onClose}>
      <DialogHeader title={t({ message: 'Download', comment: 'Download dialog header title' })} subtitle={t({
            message: 'Choose the format of your file',
            comment: 'Download dialog header subtitle',
        })} icon={<Falcon icon="download"/>}/>
      <DialogContent>{getContent()}</DialogContent>
      <DialogFooter>
        <Button size="small" appearance="secondary" disabled={downloading} onClick={onClose}>
          <Trans comment="Cancel button label">Cancel</Trans>
        </Button>
      </DialogFooter>
    </Dialog>);
}
